import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import { captureException } from '../lazySentry';
import ErrorPage from '../pages/ErrorPage';
import NetworkErrorPage from '../pages/NetworkErrorPage';
import { getLabels } from '../labels';
import { isNetworkError } from '../utils/isNetworkError';
import { isChunkLoadError, handleChunkLoadError } from '../utils/lazyWithRetry';
/**
 * Error boundary component that catches JavaScript errors anywhere in the child
 * component tree and displays a fallback UI.
 *
 * Features:
 * - Catches errors during rendering, lifecycle methods, and constructors
 * - Catches unhandled Promise rejections (via window 'unhandledrejection' event)
 * - Shows dedicated NetworkErrorPage for network-related errors
 * - Network errors are NOT auto-reported to Sentry (user can report manually)
 * - Other errors are automatically reported to Sentry
 */
export default class ErrorBoundary extends Component {
    state = {
        hasError: false,
        error: null,
    };
    /**
     * React lifecycle method called when an error is thrown in a descendant component.
     * Updates state to trigger fallback UI rendering.
     */
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    /**
     * Sets up global listener for unhandled Promise rejections.
     * This allows catching async errors that wouldn't normally reach ErrorBoundary.
     */
    componentDidMount() {
        window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
    /**
     * Cleans up the global listener to prevent memory leaks.
     */
    componentWillUnmount() {
        window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    }
    /**
     * Handles unhandled Promise rejections globally.
     * Network errors are not auto-reported to Sentry - user can report manually.
     *
     * Some async errors are expected and should not surface a crash page:
     * - `CancelledError` (TanStack Query): a query/mutation was aborted because
     *   its observer unmounted or `queryClient.clear()` ran mid-flight. This is
     *   a benign signal, not a bug.
     * - `AuthenticationError`: the cache observer in `@repo/domain-service` is
     *   already driving the logout redirect; flashing a crash page would race
     *   that navigation.
     */
    handleUnhandledRejection = (event) => {
        // A promise can reject with any value, not just an Error. An Error reason is
        // kept as-is — it already carries the stack from wherever the promise
        // rejected — and anything else is wrapped so the rest of this handler, plus
        // `onError` and `setState`, can rely on having an Error.
        const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
        // Prevent default browser error logging
        event.preventDefault();
        // Name-based match is deliberate: `@repo/error` is a leaf package and
        // must not import from `@repo/domain-service` (AuthenticationError) or
        // `@tanstack/react-query` (CancelledError). Both classes set `.name`
        // explicitly in their constructors, which is a stable public contract.
        if (error.name === 'CancelledError' || error.name === 'AuthenticationError') {
            return;
        }
        // Stale chunk after deploy — auto-refresh once instead of crashing.
        if (isChunkLoadError(error)) {
            const refreshed = handleChunkLoadError();
            if (refreshed)
                return;
        }
        // Only report non-network errors to Sentry automatically
        if (!isNetworkError(error)) {
            captureException(error, {
                tags: { source: 'unhandledrejection' },
            });
        }
        this.props.onError?.(error, { componentStack: null });
        this.setState({ hasError: true, error });
    };
    /**
     * React lifecycle method called after an error has been thrown by a descendant.
     * Used for error reporting to Sentry.
     * Network errors are not auto-reported - user can report manually via NetworkErrorPage.
     */
    componentDidCatch(error, errorInfo) {
        if (!isNetworkError(error)) {
            captureException(error, {
                extra: {
                    componentStack: errorInfo.componentStack,
                },
            });
        }
        this.props.onError?.(error, errorInfo);
    }
    /**
     * Resets the error state, allowing the component tree to re-render.
     * Can be passed to fallback components for "try again" functionality.
     */
    reset = () => {
        this.setState({ hasError: false, error: null });
    };
    /**
     * Resets error state and navigates back to the previous page.
     * Falls back to home if there is no prior history entry.
     * Used by NetworkErrorPage's "Try again" button.
     */
    handleRetry = () => {
        this.reset();
        if (window.history.length > 1) {
            window.history.back();
            return;
        }
        window.location.href = '/';
    };
    render() {
        if (this.state.hasError) {
            const { fallback } = this.props;
            const error = this.state.error ?? new Error('Unknown error');
            // Custom fallback function - receives error and reset helper
            if (typeof fallback === 'function') {
                return fallback({ error, reset: this.reset });
            }
            // Custom fallback element
            if (fallback != null) {
                return fallback;
            }
            // Default fallback: dedicated page for network errors
            if (isNetworkError(error)) {
                return _jsx(NetworkErrorPage, { error: error, onRetry: this.handleRetry });
            }
            // Default fallback: generic error page
            const message = error instanceof Error ? error.message : getLabels().common.unknown;
            const stack = error instanceof Error ? error.stack : undefined;
            return _jsx(ErrorPage, { source: "react", message: message, details: stack });
        }
        return this.props.children;
    }
}
